import React, { useState, useEffect, useCallback, useRef } from 'react';
import GameHUD from '../shared/GameHUD';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { calculateRewards } from '../../../services/gameService';
import './TowerDefenseGame.css';

// Tower types
const TOWER_TYPES = [
    { id: 'fact', name: 'Fact Tower', emoji: '📚', cost: 50, damage: 15, range: 2, cooldown: 1000, color: '#4CAF50' },
    { id: 'logic', name: 'Logic Tower', emoji: '🧠', cost: 75, damage: 25, range: 1.5, cooldown: 1500, color: '#2196F3' },
    { id: 'science', name: 'Science Tower', emoji: '🔬', cost: 100, damage: 35, range: 2.5, cooldown: 2000, color: '#9C27B0' },
    { id: 'wisdom', name: 'Wisdom Tower', emoji: '🦉', cost: 150, damage: 20, range: 3, cooldown: 800, color: '#FF9800' },
];

// Enemy types (myths)
const ENEMY_TYPES = [
    { id: 'rumor', name: 'Rumor', emoji: '💬', health: 30, speed: 2, reward: 15 },
    { id: 'myth', name: 'Myth', emoji: '👻', health: 50, speed: 1.5, reward: 25 },
    { id: 'fake', name: 'Fake News', emoji: '📰', health: 40, speed: 2.5, reward: 20 },
    { id: 'legend', name: 'Urban Legend', emoji: '🌙', health: 80, speed: 1, reward: 40 },
    { id: 'boss', name: 'Conspiracy', emoji: '👁️', health: 150, speed: 0.8, reward: 100 },
];

function TowerDefenseGame({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [gold, setGold] = useState(300);
    const [lives, setLives] = useState(10);
    const [wave, setWave] = useState(0);
    const [score, setScore] = useState(0);
    const [enemiesDefeated, setEnemiesDefeated] = useState(0);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);

    // Grid and placement
    const [towers, setTowers] = useState([]);
    const [enemies, setEnemies] = useState([]);
    const [selectedTower, setSelectedTower] = useState(null);
    const [, setProjectiles] = useState([]);

    // Wave state
    const [waveInProgress, setWaveInProgress] = useState(false);
    const [waveComplete, setWaveComplete] = useState(false);
    const totalWaves = 5;

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);
    const [showTutorial, setShowTutorial] = useState(true);

    // Game loop ref
    const gameLoopRef = useRef(null);
    const lastUpdateRef = useRef(Date.now());

    // Grid positions for towers (5x3 grid)
    const gridPositions = [];
    for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 5; col++) {
            gridPositions.push({ row, col, x: 80 + col * 120, y: 120 + row * 100 });
        }
    }

    const handleGameOver = useCallback((won) => {
        setWaveInProgress(false);
        clearInterval(gameLoopRef.current);
        setVictory(won);
        setIsComplete(true);

        const waveProgress = Math.round((wave / totalWaves) * 100);
        const rewards = calculateRewards(template, enemiesDefeated, waveProgress);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy: waveProgress,
                stats: {
                    wavesCompleted: wave,
                    enemiesDefeated,
                    towersBuilt: towers.length
                }
            });
        }
    }, [score, wave, enemiesDefeated, towers.length, template, onComplete, totalWaves]);

    // Initialize game
    useEffect(() => {
        const diffSettings = template?.difficultyScaling?.[difficulty] || {};
        setGold(diffSettings.startingGold || 300);
        setLives(10);

        setTimeout(() => setShowTutorial(false), 3000);
    }, [template, difficulty]);

    const updateGame = useCallback((delta) => {
        // Move enemies
        setEnemies(prev => {
            const updated = prev.map(enemy => {
                if (enemy.health <= 0) return enemy;

                // Move along path (simple left to right)
                const newX = enemy.x + enemy.speed * delta * 50;

                // Check if enemy reached end
                if (newX > 700) {
                    setLives(l => {
                        const newLives = l - 1;
                        if (newLives <= 0) {
                            handleGameOver(false);
                        }
                        return newLives;
                    });
                    return { ...enemy, x: newX, reachedEnd: true };
                }

                return { ...enemy, x: newX };
            });

            // Remove enemies that reached the end
            return updated.filter(e => !e.reachedEnd);
        });

        // Tower attacks
        setTowers(prev => prev.map(tower => {
            const now = Date.now();
            if (now - tower.lastFire < tower.cooldown) return tower;

            // Find enemy in range
            const towerType = TOWER_TYPES.find(t => t.id === tower.type);
            let target = null;

            setEnemies(enemies => {
                target = enemies.find(enemy => {
                    if (enemy.health <= 0) return false;
                    const dist = Math.sqrt(
                        Math.pow(enemy.x - tower.x, 2) +
                        Math.pow(enemy.y - tower.y, 2)
                    );
                    return dist < towerType.range * 60;
                });
                return enemies;
            });

            if (target) {
                // Fire projectile
                setProjectiles(proj => [...proj, {
                    id: `proj-${Date.now()}-${Math.random()}`,
                    x: tower.x,
                    y: tower.y,
                    targetId: target.id,
                    damage: towerType.damage,
                    color: towerType.color
                }]);

                return { ...tower, lastFire: now };
            }

            return tower;
        }));

        // Move projectiles and apply damage
        setProjectiles(prev => {
            return prev.filter(proj => {
                setEnemies(enemies => {
                    const target = enemies.find(e => e.id === proj.targetId);
                    if (!target) return enemies;

                    const dist = Math.sqrt(
                        Math.pow(target.x - proj.x, 2) +
                        Math.pow(target.y - proj.y, 2)
                    );

                    if (dist < 30) {
                        // Hit!
                        return enemies.map(e => {
                            if (e.id === proj.targetId) {
                                const newHealth = e.health - proj.damage;
                                if (newHealth <= 0) {
                                    // Enemy defeated
                                    setGold(g => g + e.reward);
                                    setScore(s => s + e.reward * 2);
                                    setEnemiesDefeated(ed => ed + 1);
                                    setXpEarned(xp => xp + 5);
                                }
                                return { ...e, health: newHealth };
                            }
                            return e;
                        });
                    }
                    return enemies;
                });

                return false; // Remove projectile
            });
        });

        // Remove dead enemies
        setEnemies(prev => prev.filter(e => e.health > 0));

        // Check wave complete
        setEnemies(enemies => {
            if (enemies.length === 0 && waveInProgress) {
                setWaveInProgress(false);
                setWaveComplete(true);

                if (wave >= totalWaves) {
                    setTimeout(() => handleGameOver(true), 1000);
                }
            }
            return enemies;
        });
    }, [waveInProgress, wave, totalWaves, handleGameOver]);

    // Game loop
    useEffect(() => {
        if (isPaused || isComplete || !waveInProgress) return;

        gameLoopRef.current = setInterval(() => {
            const now = Date.now();
            const delta = (now - lastUpdateRef.current) / 1000;
            lastUpdateRef.current = now;

            updateGame(delta);
        }, 50);

        return () => clearInterval(gameLoopRef.current);
    }, [isPaused, isComplete, waveInProgress, updateGame]);

    const startWave = () => {
        const nextWave = wave + 1;
        setWave(nextWave);
        setWaveComplete(false);
        setWaveInProgress(true);
        lastUpdateRef.current = Date.now();

        // Generate enemies for this wave
        const newEnemies = [];
        const enemyCount = 3 + nextWave * 2;

        for (let i = 0; i < enemyCount; i++) {
            const typeIndex = Math.min(Math.floor(i / 3), ENEMY_TYPES.length - 2);
            const type = ENEMY_TYPES[typeIndex];

            // Add boss on last wave
            const finalType = (nextWave === totalWaves && i === enemyCount - 1)
                ? ENEMY_TYPES[ENEMY_TYPES.length - 1]
                : type;

            newEnemies.push({
                id: `enemy-${nextWave}-${i}`,
                ...finalType,
                health: finalType.health * (1 + nextWave * 0.2),
                maxHealth: finalType.health * (1 + nextWave * 0.2),
                x: -50 - i * 80,
                y: 170 + Math.sin(i) * 30
            });
        }

        setEnemies(newEnemies);
    };

    const placeTower = (position) => {
        if (!selectedTower) return;

        // Check if position is taken
        const occupied = towers.some(t => t.row === position.row && t.col === position.col);
        if (occupied) return;

        const towerType = TOWER_TYPES.find(t => t.id === selectedTower);
        if (gold < towerType.cost) return;

        setTowers(prev => [...prev, {
            id: `tower-${Date.now()}`,
            type: selectedTower,
            row: position.row,
            col: position.col,
            x: position.x,
            y: position.y,
            lastFire: 0,
            cooldown: towerType.cooldown
        }]);

        setGold(prev => prev - towerType.cost);
        setSelectedTower(null);
    };

    const handleRestart = () => {
        clearInterval(gameLoopRef.current);
        setGold(template?.difficultyScaling?.[difficulty]?.startingGold || 300);
        setLives(10);
        setWave(0);
        setScore(0);
        setEnemiesDefeated(0);
        setXpEarned(0);
        setCoinsEarned(0);
        setTowers([]);
        setEnemies([]);
        setProjectiles([]);
        setSelectedTower(null);
        setWaveInProgress(false);
        setWaveComplete(false);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);
    };

    if (showTutorial) {
        return (
            <div className="td-tutorial">
                <div className="tutorial-content">
                    <h2>🏰 Truth Tower Defense</h2>
                    <p>Build towers to defeat the incoming myths!</p>
                    <div className="tutorial-steps">
                        <div className="step">1️⃣ Select a tower type</div>
                        <div className="step">2️⃣ Click on the grid to place it</div>
                        <div className="step">3️⃣ Start the wave and defend!</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="td-game">
            <GameHUD
                gameName="Truth Tower"
                score={score}
                lives={lives}
                maxLives={10}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
                showTimer={false}
            />

            <div className="td-info">
                <span className="td-topic">📚 {topic}</span>
                <span className="td-gold">🪙 {gold}</span>
                <span className="td-wave">Wave {wave}/{totalWaves}</span>
            </div>

            {/* Tower Selection */}
            <div className="tower-selection">
                {TOWER_TYPES.map(tower => (
                    <button
                        key={tower.id}
                        className={`tower-btn ${selectedTower === tower.id ? 'selected' : ''} ${gold < tower.cost ? 'disabled' : ''}`}
                        onClick={() => setSelectedTower(tower.id)}
                        disabled={gold < tower.cost}
                    >
                        <span className="tower-emoji">{tower.emoji}</span>
                        <span className="tower-name">{tower.name}</span>
                        <span className="tower-cost">🪙 {tower.cost}</span>
                        <span className="tower-stats">⚔️{tower.damage} 📏{tower.range}</span>
                    </button>
                ))}
            </div>

            {/* Game Grid */}
            <div className="td-battlefield">
                {/* Grid positions */}
                {gridPositions.map((pos, i) => {
                    const hasTower = towers.find(t => t.row === pos.row && t.col === pos.col);
                    return (
                        <div
                            key={i}
                            className={`grid-cell ${selectedTower && !hasTower ? 'placeable' : ''}`}
                            style={{ left: pos.x, top: pos.y }}
                            onClick={() => !hasTower && placeTower(pos)}
                        >
                            {hasTower && (
                                <div className="placed-tower">
                                    {TOWER_TYPES.find(t => t.id === hasTower.type)?.emoji}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Path */}
                <div className="enemy-path"></div>

                {/* Enemies */}
                {enemies.map(enemy => (
                    <div
                        key={enemy.id}
                        className="td-enemy"
                        style={{ left: enemy.x, top: enemy.y }}
                    >
                        <div className="enemy-health-bar">
                            <div
                                className="enemy-health-fill"
                                style={{ width: `${(enemy.health / enemy.maxHealth) * 100}%` }}
                            />
                        </div>
                        <span className="enemy-emoji">{enemy.emoji}</span>
                    </div>
                ))}

                {/* Base */}
                <div className="td-base">
                    <span>🏠</span>
                    <span className="base-lives">❤️ {lives}</span>
                </div>
            </div>

            {/* Wave Controls */}
            <div className="wave-controls">
                {!waveInProgress && wave < totalWaves && (
                    <button className="start-wave-btn" onClick={startWave}>
                        {wave === 0 ? '🚀 Start Game' : `🌊 Start Wave ${wave + 1}`}
                    </button>
                )}
                {waveInProgress && (
                    <div className="wave-status">
                        <span className="wave-icon">⚔️</span>
                        <span>Wave in progress... ({enemies.length} enemies)</span>
                    </div>
                )}
                {waveComplete && wave < totalWaves && (
                    <div className="wave-complete">
                        ✅ Wave {wave} Complete! Prepare for the next wave.
                    </div>
                )}
            </div>

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Truth Tower"
                    score={score}
                    stats={{
                        waves: wave,
                        enemiesDefeated
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Truth Tower"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={wave === totalWaves ? 3 : wave >= 3 ? 2 : 1}
                    accuracy={Math.round((wave / totalWaves) * 100)}
                    stats={{
                        wavesCompleted: wave,
                        enemiesDefeated,
                        towersBuilt: towers.length
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default TowerDefenseGame;
